import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Moon, Stars, Menu, X, Loader2 } from "lucide-react";
import { NAV } from "@/constants/testIds";
import useActiveDraft from "@/hooks/useActiveDraft";

export default function NavHeader({ user, onAuthClick, onLoginClick, onLogout }) {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const draft = useActiveDraft(user);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const navLinks = [
    { label: "Generator", id: "generator" },
    { label: "How It Works", id: "manifesto" },
    { label: "Voices", id: "voices" },
    { label: "Pricing", id: "pricing" },
  ];

  return (
    <motion.header
      data-testid={NAV.header}
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "py-3 glass-card border-b border-purple-500/20" : "py-5 bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 flex items-center justify-between">
        {/* Logo */}
        <button
          data-testid={NAV.logo}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2 group"
        >
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 to-amber-500 flex items-center justify-center shadow-lg shadow-purple-500/30">
            <Moon size={14} className="text-white" />
          </div>
          <span className="font-serif font-bold text-lg tracking-tight gradient-text">DreamWeaver</span>
        </button>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <button
              key={link.id}
              onClick={() => scrollTo(link.id)}
              className="text-sm text-slate-300 hover:text-amber-300 transition-colors duration-200 font-medium"
            >
              {link.label}
            </button>
          ))}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <div className="flex items-center gap-3">
              {draft && (
                <button
                  data-testid="nav-draft-badge"
                  onClick={() => navigate(`/story/${draft.storyId}`)}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-600/20 border border-purple-500/30 text-purple-300 text-xs font-medium hover:bg-purple-600/30 transition-colors"
                >
                  <Loader2 size={11} className="animate-spin" />
                  Story in progress
                </button>
              )}
              <button
                data-testid="nav-my-stories-link"
                onClick={() => navigate("/my-stories")}
                className="text-sm text-slate-300 hover:text-amber-300 transition-colors font-medium"
              >
                My Stories
              </button>
              <span className="text-sm text-slate-400">
                {user.is_subscribed ? (
                  <span className="text-amber-400 font-medium">Premium</span>
                ) : (
                  <span className="text-slate-400">{1 - (user.story_count || 0)} free {1 - (user.story_count || 0) === 1 ? "story" : "stories"} left</span>
                )}
              </span>
              <button
                onClick={onLogout}
                className="text-sm text-slate-400 hover:text-white transition-colors"
              >
                Sign out
              </button>
              <button
                data-testid={NAV.ctaButton}
                onClick={() => scrollTo("generator")}
                className="px-4 py-2 rounded-full bg-gradient-to-r from-purple-600 to-amber-500 text-white text-sm font-semibold hover:shadow-lg hover:shadow-purple-500/30 transition-all duration-200 hover:-translate-y-0.5"
              >
                Create Story
              </button>
            </div>
          ) : (
            <>
              <button
                data-testid={NAV.loginButton}
                onClick={onLoginClick || onAuthClick}
                className="text-sm text-slate-300 hover:text-white transition-colors font-medium"
              >
                Sign in
              </button>
              <button
                data-testid={NAV.ctaButton}
                onClick={onAuthClick}
                className="px-5 py-2 rounded-full bg-gradient-to-r from-purple-600 to-amber-500 text-white text-sm font-semibold hover:shadow-lg hover:shadow-purple-500/30 transition-all duration-200 hover:-translate-y-0.5"
              >
                Start Free
              </button>
            </>
          )}
        </div>

        {/* Mobile menu toggle */}
        <button
          data-testid="nav-mobile-menu-toggle"
          className="md:hidden min-h-[44px] min-w-[44px] flex items-center justify-center rounded-xl text-slate-300 hover:text-white transition-colors"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass-card border-t border-purple-500/20 mt-2"
          >
            <div className="px-4 py-4 space-y-3">
              {user && (
                <div data-testid="nav-mobile-plan-status" className="pb-3 mb-1 border-b border-purple-500/10 text-sm">
                  {user.is_subscribed ? (
                    <span className="text-amber-400 font-medium">Premium</span>
                  ) : (
                    <span className="text-slate-400">{1 - (user.story_count || 0)} free {1 - (user.story_count || 0) === 1 ? "story" : "stories"} left</span>
                  )}
                </div>
              )}
              {draft && (
                <button
                  data-testid="nav-mobile-draft-badge"
                  onClick={() => { setMenuOpen(false); navigate(`/story/${draft.storyId}`); }}
                  className="flex items-center gap-2 w-full text-left py-2 text-sm font-medium text-purple-300"
                >
                  <Loader2 size={13} className="animate-spin" /> Story in progress — tap to view
                </button>
              )}
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollTo(link.id)}
                  className="block w-full text-left text-slate-300 hover:text-amber-300 py-2 text-sm font-medium"
                >
                  {link.label}
                </button>
              ))}
              <div className="pt-2 border-t border-purple-500/20">
                {user ? (
                  <>
                    <button
                      onClick={() => { setMenuOpen(false); navigate("/my-stories"); }}
                      className="block w-full text-left text-slate-300 hover:text-amber-300 py-2 text-sm font-medium"
                    >
                      My Stories
                    </button>
                    <button
                      onClick={onLogout}
                      className="block w-full text-center py-2.5 rounded-full border border-purple-500/30 text-slate-300 text-sm"
                    >
                      Sign out
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => { setMenuOpen(false); onAuthClick(); }}
                    className="block w-full text-center py-2.5 rounded-full bg-gradient-to-r from-purple-600 to-amber-500 text-white text-sm font-semibold"
                  >
                    Start Free
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
